import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import ProjectCard from './ProjectCard';
import { iconRegistry } from './iconRegistry';
import sitesData from '../sites.json';

interface Site {
  id: string;
  url: string;
  icon: string;
  order: number;
  isNew: boolean;
}

/**
 * Resolves the copy for one site.
 *
 * The locale key is the site id verbatim (sites.<id>.*). i18next returns the
 * key itself when a translation is missing, and `features` would then be a
 * string rather than an array — which used to throw during render and blank
 * the whole page. Everything here degrades instead of throwing.
 */
const resolveSiteCopy = (t: TFunction, siteId: string) => {
  const text = (field: string) => {
    const value = t(`sites.${siteId}.${field}`);
    return value === `sites.${siteId}.${field}` ? '' : String(value);
  };

  const rawFeatures = t(`sites.${siteId}.features`, { returnObjects: true });

  return {
    title: text('title'),
    catchphrase: text('catchphrase'),
    description: text('description'),
    features: Array.isArray(rawFeatures) ? (rawFeatures as string[]) : [],
  };
};

const ProjectList: React.FC = () => {
  const { t } = useTranslation();

  // sites.json is the single source of truth for what gets published here.
  const projects = [...(sitesData.sites as Site[])]
    .sort((a, b) => a.order - b.order)
    .map((site) => {
      const Icon = iconRegistry[site.icon];
      return {
        id: site.id,
        icon: Icon ? <Icon className="w-6 h-6" /> : null,
        ...resolveSiteCopy(t, site.id),
        url: site.url,
        isNew: site.isNew,
      };
    });

  return (
    <section id="projects" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12 text-center md:text-left">
          <h2 className="text-3xl font-bold text-[#0F172A]">{t('projects.title')}</h2>
          <div className="w-12 h-1 bg-[#3B82F6] mt-4 mx-auto md:mx-0"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
          {projects.map((project) => (
            <ProjectCard key={project.id} {...project} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProjectList;